import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate } from "react-router-dom";
import LandingPage from "@/pages/LandingPage";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Leads from "@/pages/Leads";
import LeadDetails from "@/pages/LeadDetails";
import Contacts from "@/pages/Contacts";
import Clients from "@/pages/Clients";
import Tasks from "@/pages/Tasks";
import Calendar from "@/pages/Calendar";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import Integrations from "@/pages/Integrations";
import Notifications from "@/pages/Notifications";
import AiConversations from "@/pages/AiConversations";
import MobileCrmDesignPreview from "@/pages/MobileCrmDesignPreview";
import WorkPlannerDashboard from "@/pages/work-planner/WorkPlannerDashboard";
import MonthlyPlanner from "@/pages/work-planner/monthly/MonthlyPlanner";
import { WorkHubPage } from "@/pages/work-hub/WorkHubBreadcrumb";

/** Old address → Work Hub address, keeping ?query and #hash (bookmarks, notification links). */
function LegacyRedirect({ to }) {
  const location = useLocation();
  const params = useParams();
  const target = to.replace(/:(\w+)/g, (_, k) => params[k] || "");
  return <Navigate to={`${target}${location.search}${location.hash}`} replace />;
}
import CreateMonthlyPlan from "@/pages/work-planner/CreateMonthlyPlan";
import WorkPlannerList from "@/pages/work-planner/WorkPlannerList";
import ClientWiseView from "@/pages/work-planner/ClientWiseView";
import WhatsAppPage from "@/pages/whatsapp/WhatsAppPage";
// Billing + Proposals modules removed from the UI. /api/billing/settings stays
// live (salary slips read the company profile / letterhead from it).
import Profile from "@/pages/Profile";
import AccessControlManagement from "@/pages/AccessControlManagement";
import StaffAppAccess from "@/pages/attendance/StaffAppAccess";
import LeadAssignmentSettings from "@/pages/settings/LeadAssignmentSettings";
import LeadFormBuilder from "@/pages/settings/LeadFormBuilder";
import LeadSourceManagement from "@/pages/settings/LeadSourceManagement";
import LeadStageManagement from "@/pages/settings/LeadStageManagement";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import DataDeletionInstructions from "@/pages/DataDeletionInstructions";
import PublicRegister from "@/pages/PublicRegister";
import SubscriptionPlans from "@/pages/SubscriptionPlans";
import BusinessSubscriptions from "@/pages/BusinessSubscriptions";
import BusinessAccounts from "@/pages/BusinessAccounts";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";
import AttendanceProtectedRoute from "@/components/AttendanceProtectedRoute";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { BusinessProvider } from "@/contexts/BusinessContext";

// Attendance Module
import {
  AttendanceDashboard,
  PunchInOut,
  StaffProfiles,
  AttendanceRecords,
  SalaryManagement,
  AttendanceReports,
  AttendanceSettings,
  LeaveManagement,
  LeavePolicy,
  LeaveReports,
  HolidayCalendar,
  LocationTracking
} from "@/pages/attendance";

function AppRouter() {
  const location = useLocation();
  
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/login/:slug" element={<Login />} />
        <Route path="/join/:slug" element={<PublicRegister />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/data-deletion-instructions" element={<DataDeletionInstructions />} />

        {/* Protected Routes - Layout route (no path) wraps all authenticated pages */}
        <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/leads" element={<Leads />} />
          <Route path="/leads/:leadId" element={<LeadDetails />} />
          <Route path="/contacts" element={<Contacts />} />
          {/* Work Hub — client work in one workspace */}
          <Route path="/work-hub" element={<MonthlyPlanner key="overview" section="overview" />} />
          <Route path="/work-hub/clients" element={<WorkHubPage current="Clients" padded><Clients /></WorkHubPage>} />
          <Route path="/work-hub/clients/:clientId" element={<WorkHubPage current="Clients" padded><Clients /></WorkHubPage>} />
          <Route path="/work-hub/tasks" element={<WorkHubPage current="Tasks"><Tasks /></WorkHubPage>} />
          <Route path="/work-hub/planner" element={<MonthlyPlanner key="planner" section="planner" />} />
          <Route path="/work-hub/calendar" element={<MonthlyPlanner key="calendar" section="calendar" />} />
          <Route path="/work-hub/team-workload" element={<MonthlyPlanner key="workload" section="workload" />} />
          <Route path="/work-hub/reports" element={<MonthlyPlanner key="reports" section="reports" />} />
          {/* Old addresses keep working */}
          <Route path="/clients" element={<LegacyRedirect to="/work-hub/clients" />} />
          <Route path="/clients/:clientId" element={<LegacyRedirect to="/work-hub/clients/:clientId" />} />
          <Route path="/tasks" element={<LegacyRedirect to="/work-hub/tasks" />} />
          <Route path="/tasks/*" element={<LegacyRedirect to="/work-hub/tasks" />} />
          <Route path="/calendar" element={<Calendar />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/integrations" element={<Integrations />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/ai-conversations" element={<AiConversations />} />
          <Route path="/work-planner" element={<LegacyRedirect to="/work-hub/planner" />} />
          <Route path="/work-planner/performance" element={<WorkPlannerDashboard />} />
          <Route path="/work-planner/new" element={<CreateMonthlyPlan />} />
          <Route path="/work-planner/list" element={<WorkPlannerList />} />
          <Route path="/work-planner/client" element={<ClientWiseView />} />
          <Route path="/work-planner/client/:clientId" element={<ClientWiseView />} />
          <Route path="/whatsapp" element={<WhatsAppPage />} />
          <Route path="/whatsapp/:tab" element={<WhatsAppPage />} />
          <Route path="/design/mobile-crm" element={<MobileCrmDesignPreview />} />

          <Route path="/settings" element={<Settings />} />
          <Route path="/settings/access-control" element={<AccessControlManagement />} />
          <Route path="/settings/lead-assignment" element={<LeadAssignmentSettings />} />
          <Route path="/settings/lead-form-builder" element={<LeadFormBuilder />} />
          <Route path="/settings/lead-sources" element={<LeadSourceManagement />} />
          <Route path="/settings/lead-stages" element={<LeadStageManagement />} />
          <Route path="/super-admin/businesses" element={<BusinessAccounts />} />
          <Route path="/subscriptions" element={<BusinessSubscriptions />} />
          <Route path="/subscriptions/plans" element={<SubscriptionPlans />} />
          <Route path="/profile" element={<Profile />} />
          
          {/* Attendance Module Routes */}
          <Route path="/attendance" element={<AttendanceProtectedRoute><AttendanceDashboard /></AttendanceProtectedRoute>} />
          <Route path="/attendance/punch" element={<AttendanceProtectedRoute><PunchInOut /></AttendanceProtectedRoute>} />
          <Route path="/attendance/staff" element={<AttendanceProtectedRoute><StaffProfiles /></AttendanceProtectedRoute>} />
          <Route path="/attendance/records" element={<AttendanceProtectedRoute><AttendanceRecords /></AttendanceProtectedRoute>} />
          <Route path="/attendance/salary" element={<AttendanceProtectedRoute><SalaryManagement /></AttendanceProtectedRoute>} />
          <Route path="/attendance/reports" element={<AttendanceProtectedRoute><AttendanceReports /></AttendanceProtectedRoute>} />
          <Route path="/attendance/settings" element={<AttendanceProtectedRoute><AttendanceSettings /></AttendanceProtectedRoute>} />
          <Route path="/attendance/leave" element={<AttendanceProtectedRoute><LeaveManagement /></AttendanceProtectedRoute>} />
          <Route path="/attendance/leave-policy" element={<AttendanceProtectedRoute><LeavePolicy /></AttendanceProtectedRoute>} />
          <Route path="/attendance/leave-reports" element={<AttendanceProtectedRoute><LeaveReports /></AttendanceProtectedRoute>} />
          <Route path="/attendance/holidays" element={<AttendanceProtectedRoute><HolidayCalendar /></AttendanceProtectedRoute>} />
          <Route path="/attendance/location-tracking" element={<AttendanceProtectedRoute><LocationTracking /></AttendanceProtectedRoute>} />
          <Route path="/attendance/staff-app" element={<AttendanceProtectedRoute><StaffAppAccess /></AttendanceProtectedRoute>} />

          {/* Legacy redirects — removed billing/proposals URLs land on the dashboard.
              Refreshing on a URL that no longer has a route was rendering nothing
              (white screen). Every unmatched authenticated URL now lands on the dashboard. */}
          <Route path="/proposals" element={<Navigate to="/dashboard" replace />} />
          <Route path="/proposals/*" element={<Navigate to="/dashboard" replace />} />
          <Route path="/billing" element={<Navigate to="/dashboard" replace />} />
          <Route path="/billing/*" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Route>
      </Routes>
      <Toaster position="top-right" />
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BusinessProvider>
        <SubscriptionProvider>
          <div className="App">
            <BrowserRouter>
              <AppRouter />
            </BrowserRouter>
          </div>
        </SubscriptionProvider>
      </BusinessProvider>
    </ThemeProvider>
  );
}

export default App;
